import moment from 'moment';
import { toString } from '../../../core/utils';
import {
  getClaimStatusMenuOptions,
  getUnrestrictedClaimStatusOptions,
  ReclamationStatus,
  ReclamationType,
} from '../models';
import { validateStatusTransition } from './statusValidation';

// The row's status pill is driven entirely by validateStatusTransition so the options
// it offers and the transitions the save path accepts can never drift apart. The old claims
// page skips the rules and offers every status.
const getStatusEditability = (reclamation, userRole, unrestrictedStatuses = false) => {
  if (unrestrictedStatuses) {
    return {
      _allowedStatuses: getUnrestrictedClaimStatusOptions(reclamation?.status),
      _statusEditLocked: false,
      _statusEditLockReason: '',
    };
  }

  const evaluated = getClaimStatusMenuOptions(reclamation?.status).map((status) => ({
    status,
    ...validateStatusTransition(reclamation, status, userRole),
  }));
  const allowedStatuses = evaluated.filter((result) => result.valid).map((result) => result.status);
  const blocked = evaluated.find((result) => !result.valid);
  const locked = allowedStatuses.length === 0;

  return {
    _allowedStatuses: allowedStatuses,
    _statusEditLocked: locked,
    _statusEditLockReason: locked
      ? blocked?.message || 'This claim has no further status changes available.'
      : '',
  };
};

const REPAIR_DECISION_STATUSES = [ReclamationStatus.RepairApproved, ReclamationStatus.RepairDenied];

const getStatusUpdatedAt = (reclamation) => {
  const statusHistory = Array.isArray(reclamation.statusHistory)
    ? reclamation.statusHistory.find((item) => Number(item.status) === Number(reclamation.status))
    : null;

  return (
    reclamation.statusUpdatedAt ||
    reclamation.status_updated_at ||
    statusHistory?.createdAt ||
    statusHistory?.created_at ||
    null
  );
};

const isRecentlyApprovedOrDenied = (reclamation) => {
  if (!REPAIR_DECISION_STATUSES.includes(Number(reclamation.status))) return false;

  const statusUpdatedAt = getStatusUpdatedAt(reclamation);
  const hoursSinceStatusUpdated = statusUpdatedAt
    ? moment().diff(moment(statusUpdatedAt), 'hours')
    : null;

  return hoursSinceStatusUpdated !== null && hoursSinceStatusUpdated < 24;
};

export const transformReclamationForTable = (reclamation, options) => {
  const {
    reclamationType,
    damageClaimOptions,
    userRole,
    intl,
    getRewashReason,
    unrestrictedStatuses,
  } = options;

  const claimOption = damageClaimOptions.find(
    (option) => option.id === reclamation.claimDamagePartId
  );
  const type = (claimOption && claimOption.value) || '';
  const status = toString(ReclamationStatus, reclamation.status);
  let reclamationIdText = reclamation.id;
  if (reclamation.archivedAt != null) {
    reclamationIdText = reclamation.id + ' (archived)';
  }

  // Rewash type transformation
  if (reclamationType === ReclamationType.Rewash) {
    return {
      _id: reclamation.id,
      _reclamationType: toString(ReclamationType, reclamation.type),
      _issueDate: reclamation.issueDate,
      date: reclamation.issueDate,
      operation: reclamation.rewashOperation,
      rewashReason: getRewashReason(reclamation),
    };
  }

  // Claim type transformation
  const repairItemsTotal = Array.isArray(reclamation.repairItems)
    ? reclamation.repairItems.reduce((acc, item) => acc + (Number(item.price) || 0), 0)
    : null;
  const resolvedCost = repairItemsTotal !== null ? repairItemsTotal : 0;

  return {
    _id: reclamation.id,
    _reclamationType: toString(ReclamationType, reclamation.type),
    claim: reclamationIdText,
    _issueDate: reclamation.issueDate,
    date: reclamation.issueDate,
    _claimDamagePartId: reclamation.claimDamagePartId,
    _status: reclamation.status,
    _statusLabel: intl.formatMessage({ id: status }),
    ...getStatusEditability(reclamation, userRole, unrestrictedStatuses),
    _recentlyApprovedOrDenied: isRecentlyApprovedOrDenied(reclamation),
    status: intl.formatMessage({ id: status }),
    _typeLabel: type,
    type,
    _vehicle: reclamation.vehicleDetails.plate,
    vehicle: reclamation.vehicleDetails.plate,
    location: reclamation.issueSite.name,
    _customerName: reclamation.customerName,
    customerName: reclamation.customerName,
    _customerPhone: reclamation.customerPhone,
    customerPhone: reclamation.customerPhone,
    _customerEmail: reclamation.customerEmail,
    customerEmail: reclamation.customerEmail,
    _costRaw: resolvedCost,
    _costCurrency: reclamation.currency || 'USD',
    cost: resolvedCost,
  };
};

export const getStatusCellRowData = (reclamation, userRole, intl, unrestrictedStatuses) => {
  const status = toString(ReclamationStatus, reclamation.status);

  return {
    _id: reclamation.id,
    _status: reclamation.status,
    _statusLabel: intl.formatMessage({ id: status }),
    ...getStatusEditability(reclamation, userRole, unrestrictedStatuses),
    _recentlyApprovedOrDenied: isRecentlyApprovedOrDenied(reclamation),
  };
};
